interface CacheFnOptions<Fn extends (...args: never[]) => unknown> {
	ttl?: number;
	keyGenerator?: (args: Parameters<Fn>) => unknown;
	maxSize?: number;
}

class Cache<T> {
	private cache: [unknown, T][] = [];

	constructor(private readonly maxSize: number, private readonly ttl = 60000) {}

	get(key: unknown): T | undefined {
		for (let index = this.cache.length - 1; index >= 0; index -= 1) {
			if (this.cache[index][0] === key) {
				const entry = this.cache.splice(index, 1)[0];
				this.cache.push(entry);
				return entry[1];
			}
		}
	}

	set(key: unknown, value: T) {
		if (this.cache.length >= this.maxSize) {
			this.cache.shift();
		}
		this.cache.push([key, value]);
		if (this.ttl) {
			setTimeout(() => {
				this.delete(key);
			}, this.ttl);
		}
	}

	delete(key: unknown) {
		for (let index = 0; index < this.cache.length; index += 1) {
			if (this.cache[index][0] === key) {
				this.cache.splice(index, 1);
				return;
			}
		}
	}

	has(key: unknown) {
		for (let index = this.cache.length - 1; index >= 0; index -= 1) {
			if (this.cache[index][0] === key) {
				return true;
			}
		}
		return false;
	}
}

export function cacheFn<Fn extends (...args: never[]) => unknown>(fn: Fn, options: CacheFnOptions<Fn> = {}) {
	const { ttl, keyGenerator = JSON.stringify, maxSize = 100 } = options;
	const cache = new Cache<ReturnType<Fn>>(maxSize, ttl);

	return (...args: Parameters<Fn>): ReturnType<Fn> => {
		const key = keyGenerator(args);
		if (cache.has(key)) {
			return cache.get(key) as ReturnType<Fn>;
		}
		const result = fn(...args) as ReturnType<Fn>;
		cache.set(key, result);
		return result;
	};
}
